/**
 * TRUS-702: where a staff reply in a member DM goes — Portal · SMS · Email ·
 * Note — fed by GET /channels/{id}/reply-options. A closed channel stays
 * visible but disabled, with the reason as its tooltip (and in its accessible
 * name), so staff can see WHY they cannot text this member. Portal is never
 * refused, so a `no_portal_login` Portal stays pickable with that warning.
 */
import { Globe, Mail, Smartphone, StickyNote } from 'lucide-react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { cn } from '@/lib/utils';
import type { ReplyChannel, ReplyOption } from '@/services/messagingApi';
import { blockReasonText, channelLabel, isSelectableReplyOption } from './replyChannel';

const ORDER: ReplyChannel[] = ['portal', 'sms', 'email', 'note'];
const ICONS: Record<ReplyChannel, typeof Globe> = {
  portal: Globe,
  sms: Smartphone,
  email: Mail,
  note: StickyNote,
};

interface ReplyChannelPickerProps {
  options: ReplyOption[];
  value: ReplyChannel | null;
  onChange: (channel: ReplyChannel) => void;
  disabled?: boolean;
}

export function ReplyChannelPicker({ options, value, onChange, disabled = false }: ReplyChannelPickerProps) {
  const { t } = useAppTranslation();
  const byChannel = new Map(options.map((o) => [o.channel, o]));
  const shown = ORDER.filter((c) => byChannel.has(c));
  if (shown.length === 0) return null;
  return (
    <div
      role="radiogroup"
      aria-label={t('messaging.replyChannel.pickerLabel', 'Reply via')}
      className="inline-flex flex-wrap items-center gap-0.5 rounded-md border bg-muted/40 p-0.5"
      data-testid="reply-channel-picker"
    >
      {shown.map((c) => {
        const option = byChannel.get(c)!;
        const label = channelLabel(c, t) || option.label;
        // Portal with no login stays selectable (the server never refuses it);
        // its reason is a warning, not a block.
        const reason = option.allowed ? null : blockReasonText(option.reason, t);
        const selectable = isSelectableReplyOption(option);
        const selected = value === c;
        const Icon = ICONS[c];
        return (
          <button
            key={c}
            type="button"
            role="radio"
            aria-checked={selected}
            aria-label={reason ? `${label}: ${reason}` : label}
            title={reason ?? undefined}
            disabled={disabled || !selectable}
            onClick={() => onChange(c)}
            data-channel={c}
            data-testid={`reply-channel-${c}`}
            className={cn(
              'inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium transition-colors',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400',
              'disabled:cursor-not-allowed disabled:opacity-50',
              selected
                ? c === 'note'
                  ? 'bg-yellow-100 text-yellow-900 shadow-sm dark:bg-yellow-900/40 dark:text-yellow-100'
                  : 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            <Icon className="h-3.5 w-3.5" aria-hidden="true" />
            {label}
          </button>
        );
      })}
    </div>
  );
}
